import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { ChannelType, type CreateDefaultConfigFormData, type PricePlanTemplate, SupportType } from './types'

type TemplateOption = Pick<PricePlanTemplate, 'id' | 'name'>

type DefaultConfigFormProps = {
  templates: TemplateOption[]
  supportedCurrencies: string[]
  supportedPartners: string[]
  onSubmit: (formData: CreateDefaultConfigFormData) => void
}

export const DefaultConfigForm = ({ templates, supportedCurrencies, supportedPartners, onSubmit }: DefaultConfigFormProps) => {
  const { control, handleSubmit, watch, reset } = useForm<CreateDefaultConfigFormData>({
    defaultValues: createDefaultValues(ChannelType.AGENCY),
  })

  const channelType = watch('channel.type')
  const currencySupportType = watch('currencySupport.type')

  return (
    <Stack spacing={2}>
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <Typography variant="h2">Create new default config</Typography>
        <Typography variant="p2Medium">Config details</Typography>
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          <Controller
            name="channel.type"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <FormControl fullWidth>
                <InputLabel id="channel-type-label">Channel type</InputLabel>
                <Select
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value as ChannelType

                    reset(createDefaultValues(value))
                  }}
                  id="channel-type"
                  label="Channel type"
                  variant="outlined"
                  labelId="channel-type-label"
                >
                  <MenuItem value={ChannelType.AGENCY}>Agency</MenuItem>
                  <MenuItem value={ChannelType.PARTNER}>Partner</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {channelType === ChannelType.AGENCY ? (
            <Controller
              name="channel.channelId"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth id="channel-id" label="Agency ID" variant="outlined" />}
            />
          ) : (
            <Controller
              name="channel.channelId"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  fullWidth
                  id="channel-id"
                  options={supportedPartners}
                  value={field.value || undefined}
                  disableClearable
                  onChange={(_event, selected) => {
                    field.onChange(selected ?? '')
                  }}
                  renderInput={(params) => <TextField {...params} label="Partner" variant="outlined" />}
                />
              )}
            />
          )}

          <Controller
            name="templateId"
            control={control}
            render={({ field }) => {
              const templateOptions = templates.map((t) => ({ label: t.name, value: t.id }))
              return (
                <Autocomplete
                  fullWidth
                  id="template-id"
                  options={templateOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={templateOptions.find((o) => o.value === field.value) ?? undefined}
                  disableClearable
                  onChange={(_event, selected) => {
                    if (selected) {
                      field.onChange(selected.value)
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Template" variant="outlined" />}
                />
              )
            }}
          />

          <Controller
            name="currencySupport.type"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <FormControl fullWidth>
                <InputLabel id="currency-support-type-label">Currency support</InputLabel>
                <Select
                  {...field}
                  onChange={(e) => onChange(e.target.value as SupportType)}
                  id="currency-support-type"
                  label="Currency support"
                  variant="outlined"
                  labelId="currency-support-type-label"
                >
                  <MenuItem value={SupportType.ALL}>All</MenuItem>
                  <MenuItem value={SupportType.SPECIFIC}>Specific</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {currencySupportType === SupportType.SPECIFIC && (
            <Controller
              name="currencySupport.currencies"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <FormControl fullWidth>
                  <InputLabel id="currencies-label">Currencies</InputLabel>
                  <Select
                    {...field}
                    onChange={(e) => onChange(e.target.value as string[])}
                    id="currencies"
                    label="Currencies"
                    variant="outlined"
                    labelId="currencies-label"
                    multiple
                    value={value ?? []}
                  >
                    {supportedCurrencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          )}

          <Button fullWidth type="submit" variant="contained" color="primary">
            Create
          </Button>
        </Stack>
      </form>
    </Stack>
  )
}

const createDefaultValues = (channelType: ChannelType): CreateDefaultConfigFormData => ({
  channel: {
    type: channelType,
    channelId: '',
  },
  templateId: '',
  currencySupport: {
    type: SupportType.ALL,
    currencies: [],
  },
})
